import { useContext, useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { PlusCircle } from 'react-feather';
import styled from '@emotion/styled';
import type { WidgetInstance } from '@trinity/chat';
import { WidgetSourceType } from '@trinity/chat';
import { ChartSaveModal } from '@trinity/chat-components/components/WidgetGrid/ChartSaveModal';
import { WidgetPlacementModal } from '@trinity/chat-components/components/WidgetGrid/WidgetPlacementModal';
import { ChartWidgetApiContext } from '@trinity/chat-components/containers/ChartWidget/Context';
import { chatDataToQuickChartData } from '@trinity/chat-components/utils/chart';
import { ChartInfoContext, ChartThemeIdContext } from './context';

export function AddToDashboardButton(): React.ReactNode {
  const chartWidgetApi = useContext(ChartWidgetApiContext);
  const chartInfoState = useContext(ChartInfoContext);
  const chartThemeState = useContext(ChartThemeIdContext);

  const chart = chartInfoState?.[0];
  const selectedThemeId = chartThemeState?.[0] ?? null;

  const [saveOpen, setSaveOpen] = useState(false);
  const [placementModalOpen, setPlacementModalOpen] = useState(false);
  const [savedWidget, setSavedWidget] = useState<WidgetInstance | null>(null);

  const quickChartData = useMemo(() => {
    if (!chart) return null;
    return chatDataToQuickChartData(chart);
  }, [chart]);

  const handleClick = useCallback(() => {
    setSaveOpen(true);
  }, []);

  const handleSaveSuccess = useCallback((newWidget: WidgetInstance) => {
    setSavedWidget(newWidget);
    setPlacementModalOpen(true);
  }, []);

  const closePlacementModal = useCallback(() => {
    setPlacementModalOpen(false);
    setSavedWidget(null);
  }, []);

  // Safety check: if ChartInfoContext is not available, return null (old Brand Insights experience)
  if (!chartInfoState || !chart) return null;

  // No provider in tree → hide button (no-op for apps/chat)
  if (!chartWidgetApi) return null;

  if (!quickChartData) return null;

  const showModal = saveOpen || placementModalOpen;

  return (
    <>
      <StyledIconButton onClick={handleClick} aria-label="Add to dashboard">
        <StyledPlusIcon />
      </StyledIconButton>

      {showModal && createPortal(
        <ModalLayer>
          <ChartSaveModal
            open={saveOpen}
            onClose={() => setSaveOpen(false)}
            chartData={quickChartData}
            chartTitle={chart.chart.chart.title}
            selectedThemeId={selectedThemeId}
            sourceMetadata={{ type: WidgetSourceType.BrandInsights }}
            onSaveSuccess={handleSaveSuccess}
            disablePortal
          />

          {savedWidget && (
            <WidgetPlacementModal
              open={placementModalOpen}
              onClose={closePlacementModal}
              widget={savedWidget}
              onPlacementSuccess={closePlacementModal}
              disablePortal
            />
          )}
        </ModalLayer>,
        document.body,
      )}
    </>
  );
}

const StyledIconButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  '&:focus-visible': {
    outline: '2px solid var(--primary-color)',
    outlineOffset: '2px',
  },
});

const StyledPlusIcon = styled(PlusCircle)({
  color: 'var(--gray-d)',
});

const ModalLayer = styled.div({
  position: 'relative',
  zIndex: 1301,
});
